import React from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { FolderKanban, Euro, ShieldCheck, ArrowRight } from 'lucide-react';
import SEOHead from '../components/seo/SEOHead';
import Breadcrumb from '../components/seo/Breadcrumb';
import { PROJECTS, HISTORIC_PROJECTS, PORTFOLIO_STATS } from '../data/projects';
import { isEnglishPath } from '../utils/i18nRoutes';

const ALTERNATES = [
  { hreflang: 'es', href: '/proyectos' },
  { hreflang: 'en', href: '/en/projects' },
  { hreflang: 'x-default', href: '/proyectos' },
];

const TEXT = {
  es: {
    seoTitle: 'Proyectos I+D+i | Cartera de innovación de Valoriza',
    seoDescription: 'Cartera de I+D+i de Valoriza: 27 proyectos (2012–2029) con fondos CDTI, NextGenerationEU, LIFE y consorcios. Presupuesto de cartera ~16,4 M€.',
    jsonLdName: 'Proyectos de I+D+i de Valoriza',
    jsonLdDesc: 'Cartera de 27 proyectos de I+D+i de Valoriza (2012–2029) con fondos CDTI, NextGenerationEU, LIFE y consorcios.',
    breadcrumb: [{ label: 'Inicio', to: '/' }, { label: 'Proyectos' }],
    tag: 'I+D+i',
    h1a: 'Proyectos financiados, impacto',
    h1b: 'real',
    lead: 'Cartera de I+D+i de Valoriza: 27 proyectos (2012–2029) con fondos CDTI, NextGenerationEU, LIFE y consorcios.',
    statProjects: 'Proyectos I+D',
    statBudget: 'Cartera',
    statPatents: 'Títulos de PI',
    groups: { Activo: 'Proyectos activos', 'En justificación': 'En justificación', Completado: 'Completados recientes' },
    viewProject: 'Ver proyecto',
    historicTitle: 'Histórico (2012–2021)',
    btnPatents: 'Ver patentes y propiedad industrial',
    btnContact: 'Contactar equipo I+D',
    projectsPath: '/proyectos',
    patentsPath: '/patentes',
    contactPath: '/contacto',
  },
  en: {
    seoTitle: 'R&D Projects | Valoriza’s innovation portfolio',
    seoDescription: 'Valoriza’s R&D portfolio: 27 projects (2012–2029) funded by CDTI, NextGenerationEU, LIFE and consortia. Portfolio budget ~€16.4M.',
    jsonLdName: 'Valoriza’s R&D projects',
    jsonLdDesc: 'Portfolio of 27 R&D projects at Valoriza (2012–2029) funded by CDTI, NextGenerationEU, LIFE and consortia.',
    breadcrumb: [{ label: 'Home', to: '/en' }, { label: 'Projects' }],
    tag: 'R&D',
    h1a: 'Funded projects, real',
    h1b: 'impact',
    lead: 'Valoriza’s R&D portfolio: 27 projects (2012–2029) funded by CDTI, NextGenerationEU, LIFE and consortia.',
    statProjects: 'R&D projects',
    statBudget: 'Portfolio',
    statPatents: 'IP titles',
    groups: { Activo: 'Active projects', 'En justificación': 'In closeout', Completado: 'Recently completed' },
    viewProject: 'View project',
    historicTitle: 'Historic (2012–2021)',
    btnPatents: 'See patents and industrial property',
    btnContact: 'Contact the R&D team',
    projectsPath: '/en/projects',
    patentsPath: '/patentes',
    contactPath: '/en/contact',
  },
};

const GROUP_ORDER = ['Activo', 'En justificación', 'Completado'];

function ProjectCard({ p, i, viewProjectLabel, locale }) {
  const desc = locale === 'en' ? p.descEn : p.desc;
  const projectPath = locale === 'en' ? `/en/projects/${p.slug}` : `/proyectos/${p.slug}`;
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: (i % 6) * 0.06 }}
      className="liquid-glass rounded-2xl p-6 flex flex-col h-full"
    >
      <h3 className="text-base font-bold text-black dark:text-white/90 mb-2">{p.name}</h3>
      <p className="text-sm text-gray-700 dark:text-white/65 leading-relaxed mb-4 flex-1">{desc}</p>
      <Link to={projectPath} className="inline-flex items-center gap-1.5 text-sm font-semibold hover:gap-2.5 transition-all" style={{ color: '#8DC21F' }} data-testid={`project-link-${p.slug}`}>
        {viewProjectLabel} <ArrowRight size={14} />
      </Link>
    </motion.div>
  );
}

export default function ProyectosPage() {
  const { pathname } = useLocation();
  const locale = isEnglishPath(pathname) ? 'en' : 'es';
  const t = TEXT[locale];
  const path = locale === 'en' ? '/en/projects' : '/proyectos';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: t.jsonLdName,
    description: t.jsonLdDesc,
    url: `https://innovation.valorizasm.com${path}`,
  };

  return (
    <>
      <SEOHead
        title={t.seoTitle}
        description={t.seoDescription}
        path={path}
        locale={locale}
        alternates={ALTERNATES}
        jsonLd={[jsonLd]}
      />
      <div className="pt-32 pb-24 v-container" data-testid="proyectos-page">
        <Breadcrumb items={t.breadcrumb} />

        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-3xl mb-10">
          <span className="v-tag green mb-6 inline-flex">{t.tag}</span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-black dark:text-white/95 mb-6 tracking-tight leading-tight" data-testid="proyectos-h1">
            {t.h1a} <span style={{ fontFamily: "'Instrument Serif', serif", fontStyle: 'italic', color: '#8DC21F' }}>{t.h1b}</span>
          </h1>
          <p className="text-base sm:text-lg text-gray-700 dark:text-white/65 leading-relaxed">{t.lead}</p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-16 max-w-2xl">
          <div className="glass-card p-5 text-center">
            <FolderKanban size={18} className="mx-auto mb-2" style={{ color: '#8DC21F' }} />
            <div className="text-xl font-bold" style={{ color: '#8DC21F' }}>{PORTFOLIO_STATS.total}</div>
            <div className="text-[10px] uppercase tracking-wide text-gray-600 dark:text-white/50">{t.statProjects}</div>
          </div>
          <div className="glass-card p-5 text-center">
            <Euro size={18} className="mx-auto mb-2" style={{ color: '#8DC21F' }} />
            <div className="text-xl font-bold" style={{ color: '#8DC21F' }}>{PORTFOLIO_STATS.presupuesto}</div>
            <div className="text-[10px] uppercase tracking-wide text-gray-600 dark:text-white/50">{t.statBudget}</div>
          </div>
          <div className="glass-card p-5 text-center">
            <ShieldCheck size={18} className="mx-auto mb-2" style={{ color: '#8DC21F' }} />
            <div className="text-xl font-bold" style={{ color: '#8DC21F' }}>{PORTFOLIO_STATS.patentes}</div>
            <div className="text-[10px] uppercase tracking-wide text-gray-600 dark:text-white/50">{t.statPatents}</div>
          </div>
        </div>

        {GROUP_ORDER.map((key) => {
          const items = PROJECTS.filter((p) => p.estado === key);
          if (items.length === 0) return null;
          return (
            <section key={key} className="mb-14">
              <h2 className="text-xl font-bold text-black dark:text-white/90 mb-5">{t.groups[key]}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {items.map((p, i) => <ProjectCard key={p.slug} p={p} i={i} viewProjectLabel={t.viewProject} locale={locale} />)}
              </div>
            </section>
          );
        })}

        <section className="mb-10">
          <h2 className="text-xl font-bold text-black dark:text-white/90 mb-5">{t.historicTitle}</h2>
          <div className="flex flex-wrap gap-3">
            {HISTORIC_PROJECTS.map((h) => (
              <div key={h.name} className="glass-card px-4 py-2.5 text-xs" data-testid={`historic-project-${h.name}`}>
                <span className="font-semibold text-gray-700 dark:text-white/70">{h.name}</span>
                <span className="text-gray-500 dark:text-white/40 ml-2">{h.years}</span>
              </div>
            ))}
          </div>
        </section>

        <div className="flex flex-wrap gap-4 mt-10">
          <Link to={t.patentsPath} className="btn-secondary flex items-center gap-2">{t.btnPatents}</Link>
          <Link to={t.contactPath} className="btn-primary flex items-center gap-2">{t.btnContact} <ArrowRight size={15} /></Link>
        </div>
      </div>
    </>
  );
}
